'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SubsidiaryCompanyItem } from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { flagUrlForCountry } from '@/lib/country-flags';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { LocaleLink } from '@/components/locale-link';

/**
 * Table of Beteiligungen (subsidiaries). Rows link to the subsidiary's own page when
 * `register_court` is known. Guests see the first three rows with the third blurred,
 * plus a sign-in CTA showing the total count.
 */
export function SubsidiariesTabContent({
  subsidiaries,
  isAuthenticated,
  euId,
}: {
  subsidiaries: SubsidiaryCompanyItem[];
  isAuthenticated: boolean;
  euId: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();
  // Stable identity required — see the note on the Gesellschafter table: an
  // inline .slice() per render makes react-table's auto-reset loop forever.
  const visibleData = useMemo(
    () => (isAuthenticated ? subsidiaries : subsidiaries.slice(0, 3)),
    [isAuthenticated, subsidiaries],
  );
  const hasMore = !isAuthenticated && subsidiaries.length > 2;

  const columns = useMemo<ColumnDef<SubsidiaryCompanyItem>[]>(
    () => [
      {
        id: 'company',
        header: t('companyDetail.participationCompany'),
        cell: (info) => {
          const p = info.row.original;
          const hasLink = Boolean(p.eu_id && p.register_court);
          const flagUrl = flagUrlForCountry(p.country_code);
          return (
            <div className="flex items-start gap-1.5">
              {flagUrl ? (
                <img
                  src={flagUrl}
                  alt={p.country_code ?? ''}
                  className="h-3.5 w-3.5 shrink-0 mt-0.5 rounded-sm object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <Building2
                className={`h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5${flagUrl ? ' hidden' : ''}`}
              />
              {hasLink ? (
                <LocaleLink
                  href={generateCompanySlug(
                    {
                      name: p.name,
                      registerCourt: p.register_court!,
                      registerType: p.register_type ?? undefined,
                      registerId: p.register_id ?? undefined,
                    },
                    locale,
                  )}
                  className="text-xs hover:underline break-words whitespace-normal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.name}
                </LocaleLink>
              ) : (
                <span className="text-xs break-words whitespace-normal">
                  {p.name}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'location',
        header: t('companyDetail.participationLocation'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.sitz || '—'}
          </span>
        ),
        size: 100,
      },
    ],
    [t, locale],
  );

  const table = useReactTable({
    data: visibleData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative">
      <DataGridContainer
        border={false}
        className={cn(
          'overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5',
          isAuthenticated ? 'max-h-[320px]' : '',
          !isAuthenticated &&
            '[&_tbody_tr:nth-child(3)]:blur-[5px] [&_tbody_tr:nth-child(3)]:select-none',
        )}
      >
        <DataGrid<SubsidiaryCompanyItem>
          table={table}
          recordCount={visibleData.length}
          onRowClick={
            isAuthenticated
              ? (row) => {
                  if (row.register_court) {
                    window.location.href = generateCompanySlug(
                      {
                        name: row.name,
                        registerCourt: row.register_court,
                        registerType: row.register_type ?? undefined,
                        registerId: row.register_id ?? undefined,
                      },
                      locale,
                    );
                  }
                }
              : undefined
          }
          tableLayout={{
            headerSticky: true,
            headerBorder: true,
            rowBorder: true,
            stripped: false,
            dense: true,
            width: 'auto',
            columnsResizable: false,
            cellBorder: false,
          }}
          tableClassNames={{
            base: 'w-full',
            header: 'text-xs',
            headerRow: 'h-8 !bg-muted',
            body: 'text-xs text-foreground',
            bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
          }}
        >
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGridTable />
          </div>
        </DataGrid>
      </DataGridContainer>
      {hasMore && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'subsidiaries', euId, 'login');
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeAllSubsidiaries',
              'Sign in to see all {{count}} subsidiaries',
              { count: subsidiaries.length },
            )}
          </a>
        </div>
      )}
    </div>
  );
}
